import * as d3 from 'd3';
import { select as d3Select } from 'd3-selection'
import { useLayoutEffect, useMemo, useRef } from 'react';
// Tools
import { no_propagate } from './utils';
// Sub-components
import TripBarVisible from './TripBarVisible';
// Constants
import * as constants from './constants';

const MAX_BAR_LENGTH = 70;
const INVISIBLE_EXTRA_BAR_LENGTH = MAX_BAR_LENGTH / 2;

const interpolateRainbowTruncated = (x) => d3.interpolateRainbow(1 - (1-x)*0.8);

const TripsBar = ({ orient, trips, num_trips, hammock_diary_urls, length, highlighted_trip, highlighted_person, selected_trip, selected_person, trip_initial_anim_delays, set_focused_trip, set_selected_trip}) => {

  const colors = d3.quantize(interpolateRainbowTruncated, num_trips);
  const bar_thickness = length / num_trips;

  const max_trip_size = useMemo(() => {
    let max = 0;
    for (let trip_index in trips) {
      trip_index = parseInt(trip_index);
      const person_trips = trips[trip_index].person_trips;
      let num_people = 0;
      for (let pt in person_trips) {
        num_people += person_trips[pt].person.group_size;
      }
      if (num_people > max) {
        max = num_people;
      }
    }
    return max;
  }, [trips]);

  let trip_bars = [];
  for (let trip_index in trips) {
    trip_index = parseInt(trip_index);

    const person_trips = trips[trip_index].person_trips;
    let trip_is_highlight_noteworthy = (highlighted_trip === trip_index);
    for (let person_trip_index in person_trips) {
      if (person_trips[person_trip_index].person_index === highlighted_person) {
        trip_is_highlight_noteworthy = true;
        break;
      }
    }
    let trip_is_select_noteworthy = (selected_trip === trip_index);
    for (let person_trip_index in person_trips) {
      if (person_trips[person_trip_index].person_index === selected_person) {
        trip_is_select_noteworthy = true;
        break;
      }
    }

    let num_people = 0;
    for (let pt in person_trips) {
      num_people += person_trips[pt].person.group_size;
    }
    const bar_length = MAX_BAR_LENGTH * Math.log10((0.75*num_people+0.5))/Math.log10(0.75*max_trip_size+0.5);

    const popout = trip_is_select_noteworthy ? 10 : 0;
    // Always render trip bars as if we're in landscape view, then transform by rotating if we're portrait.
    // Slight exception for invisible_x since we need to stretch it out in opposite direction for the two
    // orientations and invisible_width to account for popout.
    const width = bar_length + popout;
    const invisible_width = MAX_BAR_LENGTH + INVISIBLE_EXTRA_BAR_LENGTH + (orient === "landscape" ? popout : 0);
    const invisible_x = orient === "landscape" ? -popout : -INVISIBLE_EXTRA_BAR_LENGTH;
    const height = bar_thickness;
    const x = -popout;
    const y = -length/2 + bar_thickness * trip_index;
    trip_bars.push(<rect key={`invisible_${trip_index}`}
                         className='backgroundBlue'
                         data-trip_index={trip_index}
                         height={height}
                         width={invisible_width}
                         y={y}
                         x={invisible_x}
                         onMouseEnter={() => set_focused_trip(trip_index)}
                         onMouseLeave={() => set_focused_trip(null)}
                         onClick={no_propagate(() => set_selected_trip(trip_index))}/>);
    trip_bars.push(<TripBarVisible key={`visible_${trip_index}`}
                                   height={height}
                                   y={y}
                                   new_width={width}
                                   new_x={x}
                                   fill={colors[trip_index]}
                                   opacity={trip_is_highlight_noteworthy ? "1" : "0.5"}
                                   link_icon={hammock_diary_urls[trip_index] !== ""}
                                   link_font_size={bar_thickness*0.8}
                                   initial_anim_delay={trip_initial_anim_delays[trip_index]}/>);
  }

  const [text_x, text_y] = (orient === "portrait") ? [-length/2, 0] : [0, -length/2];
  const transform_str = (orient === "portrait") ? `translate(0, ${MAX_BAR_LENGTH}) rotate(-90)` : "";

  // Initial animation for "Trips" text
  const text_ref = useRef();
  useLayoutEffect(() => {
    d3Select(text_ref.current)
      .attr('opacity', 0)
      .transition()
      .duration(constants.TRIPBARTEXT_INITIAL_ANIM_DURATION)
      .delay(constants.TRIPBARTEXT_INITIAL_ANIM_DELAY)
      .attr('opacity', 1);
  }, []);

  return (
    <g>
      <text ref={text_ref} className="link_icon" dy="-0.5em" x={text_x} y={text_y} style={{fontSize: 18}} fill="white">Trips</text>
      <g transform={transform_str}>
        {trip_bars}
      </g>
    </g>
  );
};

export default TripsBar;
